import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Stack,
  Pagination,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField
} from '@mui/material';
import instance from '../../utils/axiosConfig';

const actionTypeColors = {
  VERIFY_DISTRIBUTOR: 'success',
  REJECT_VERIFICATION: 'error',
  APPROVE_INVOICE: 'success',
  REJECT_INVOICE: 'error',
  CONFIRM_REFUND: 'info',
  CANCEL_REFUND: 'warning',
  MODIFY_PLAN: 'info',
  DELETE_PLAN: 'error',
  CREATE_PLAN: 'success',
  MODIFY_USER: 'info',
  DELETE_USER: 'error',
  CREATE_MODERATOR: 'success',
  DELETE_MODERATOR: 'error'
};

function Logs() {
  const [logs, setLogs] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0
  });
  const [filters, setFilters] = useState({
    actionType: '',
    startDate: '',
    endDate: '',
    targetModel: ''
  });

  const fetchLogs = async (page = 1) => {
    try {
      let query = `page=${page}`;
      if (filters.actionType) query += `&actionType=${filters.actionType}`;
      if (filters.targetModel) query += `&targetModel=${filters.targetModel}`;
      if (filters.startDate) query += `&startDate=${filters.startDate}`;
      if (filters.endDate) query += `&endDate=${filters.endDate}`;

      const response = await instance.get(`/api/logs?${query}`);
      setLogs(response.data.logs);
      setPagination(response.data.pagination);
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Error al cargar los logs'
      });
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('es-AR', {
      dateStyle: 'medium',
      timeStyle: 'medium'
    });
  };

  return (
    <Box>
      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Filtros
        </Typography>
        <Stack direction="row" spacing={2}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Tipo de Acción</InputLabel>
            <Select
              value={filters.actionType}
              label="Tipo de Acción"
              onChange={(e) => handleFilterChange('actionType', e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="VERIFY_DISTRIBUTOR">Verificar Distribuidor</MenuItem>
              <MenuItem value="REJECT_VERIFICATION">Rechazar Verificación</MenuItem>
              <MenuItem value="APPROVE_INVOICE">Aprobar Factura</MenuItem>
              <MenuItem value="REJECT_INVOICE">Rechazar Factura</MenuItem>
              <MenuItem value="CONFIRM_REFUND">Confirmar Reembolso</MenuItem>
              <MenuItem value="CANCEL_REFUND">Cancelar Reembolso</MenuItem>
              <MenuItem value="MODIFY_PLAN">Modificar Plan</MenuItem>
              <MenuItem value="DELETE_PLAN">Eliminar Plan</MenuItem>
              <MenuItem value="CREATE_PLAN">Crear Plan</MenuItem>
              <MenuItem value="MODIFY_USER">Modificar Usuario</MenuItem>
              <MenuItem value="DELETE_USER">Eliminar Usuario</MenuItem>
              <MenuItem value="CREATE_MODERATOR">Crear Moderador</MenuItem>
              <MenuItem value="DELETE_MODERATOR">Eliminar Moderador</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Modelo Objetivo</InputLabel>
            <Select
              value={filters.targetModel}
              label="Modelo Objetivo"
              onChange={(e) => handleFilterChange('targetModel', e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="Distributor">Distribuidor</MenuItem>
              <MenuItem value="Invoice">Factura</MenuItem>
              <MenuItem value="Plan">Plan</MenuItem>
              <MenuItem value="Admin">Administrador</MenuItem>
              <MenuItem value="Moderator">Moderador</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Fecha Inicio"
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            label="Fecha Fin"
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Stack>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Fecha</TableCell>
              <TableCell>Acción</TableCell>
              <TableCell>Realizado Por</TableCell>
              <TableCell>Modelo</TableCell>
              <TableCell>Detalles</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log) => (
              <TableRow key={log._id}>
                <TableCell>{formatDate(log.timestamp)}</TableCell>
                <TableCell>
                  <Chip
                    label={log.actionType.replace(/_/g, ' ')}
                    color={actionTypeColors[log.actionType]}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {log.performedBy.userName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {log.performedBy.userModel}
                  </Typography>
                </TableCell>
                <TableCell>{log.targetModel}</TableCell>
                <TableCell>
                  <Typography variant="body2" component="pre" sx={{ 
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    fontSize: '0.75rem'
                  }}>
                    {JSON.stringify(log.details, null, 2)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {logs.length === 0 && (
        <Typography sx={{ mt: 2, textAlign: 'center' }}>
          No se encontraron logs
        </Typography>
      )}

      {logs.length > 0 && (
        <Stack 
          direction="row" 
          justifyContent="center" 
          alignItems="center" 
          spacing={2}
          sx={{ mt: 3 }}
        >
          <Pagination
            count={pagination.totalPages}
            page={pagination.currentPage}
            onChange={(e, page) => fetchLogs(page)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Stack>
      )}
    </Box>
  );
}

export default Logs; 